(function initBulkAddProducts() {
  if (customElements.get('bulk-add-products')) {
    return;
  }

  class BulkAddProducts extends HTMLElement {
    constructor() {
      super();

      this.ADD_BUTTON = '<button type="button" class="bulk-add-products__add-row">+</button>';
      this.REMOVE_BUTTON = '<button type="button" class="bulk-add-products__remove-row">-</button>';
    }

    get rowTemplate() {
      return `
        <div class="bulk-add-products__manual-form--row">
          <div class="bulk-add-products__manual-field--wrapper">
            <div class="bulk-add-products__manual-field">
              <input type="text" name="sku" placeholder="SKU" class="bulk-add-products-input">
            </div>
            <div class="bulk-add-products__manual-field">
              <input 
                type="number" 
                name="quantity" 
                placeholder="${this.t.quantity}" 
                min="1" 
                class="bulk-add-products-input"
              >
            </div>
          </div>
        </div>
      `;
    }

    get t() {
      return window.theme.locales.bulkCsv;
    }

    connectedCallback() {
      this.abortController = new AbortController();

      this.showButton = this.querySelector('#show-bulk-products-modal');
      this.closeButton = this.querySelector('#close-bulk-products-modal');
      this.modal = this.querySelector('.bulk-add-products__modal');
      this.modalContent = this.querySelector('.bulk-add-products__modal--content');
      this.fileInput = this.querySelector('#csv-file');
      this.chooseFileButton = this.querySelector('#choose-csv-file');
      this.processButton = this.querySelector('#process-csv');
      this.fileNameSpan = this.querySelector('#uploaded-file-name');
      this.manualRowsContainer = this.querySelector('#manual-rows');
      this.addManualProductsButton = this.querySelector('#add-manual-products');
      this.switchToCSVButton = this.querySelector('#switchToCSV');
      this.switchToManualButton = this.querySelector('#switchToManual');
      this.csvForm = this.querySelector('#csvUpload');
      this.manualForm = this.querySelector('#manualUpload');
      this.visibleModalBodyClass = 'bulk-add-products-modal-visible';

      this.bindEvents();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    bindEvents() {
      this.boundShowModal = this.showModal.bind(this);
      this.boundHideModal = this.hideModal.bind(this);
      this.boundHandleModalClick = this.handleModalClick.bind(this);
      this.boundTriggerFileInput = this.triggerInputFile.bind(this);
      this.boundHandleFileChange = this.handleFileChange.bind(this);
      this.boundProcessCSV = this.processCSV.bind(this);
      this.boundAddRow = this.addManualRow.bind(this);
      this.boundRemoveRow = this.removeManualRow.bind(this);
      this.boundProcessManual = this.processManualForm.bind(this);
      this.boundSwitchToCSV = this.switchToCSV.bind(this);
      this.boundSwitchToManual = this.switchToManual.bind(this);

      const { signal } = this.abortController;

      if (this.showButton) {
        this.showButton.addEventListener('click', this.boundShowModal, { signal });
      }

      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.boundHideModal, { signal });
      }

      if (this.modal) {
        this.modal.addEventListener('click', this.boundHandleModalClick, { signal });
      }

      if (this.chooseFileButton) {
        this.chooseFileButton.addEventListener('click', this.boundTriggerFileInput, {
          signal,
        });
      }

      if (this.fileInput) {
        this.fileInput.addEventListener('change', this.boundHandleFileChange, { signal });
      }

      if (this.processButton) {
        this.processButton.addEventListener('click', this.boundProcessCSV, { signal });
      }

      if (this.addManualProductsButton) {
        this.addManualProductsButton.addEventListener('click', this.boundProcessManual, {
          signal,
        });
      }

      if (this.switchToCSVButton) {
        this.switchToCSVButton.addEventListener('click', this.boundSwitchToCSV, {
          signal,
        });
      }

      if (this.switchToManualButton) {
        this.switchToManualButton.addEventListener('click', this.boundSwitchToManual, {
          signal,
        });
      }

      this.bindManualRowEvents();
    }

    showModal() {
      document.body.classList.add(this.visibleModalBodyClass);
    }

    hideModal() {
      document.body.classList.remove(this.visibleModalBodyClass);
    }

    handleModalClick(event) {
      if (!this.modalContent.contains(event.target)) {
        this.hideModal();
      }
    }

    triggerInputFile() {
      if (this.fileInput) {
        this.fileInput.click();
      }
    }

    handleFileChange(event) {
      const file = event.target.files[0];
      const defaultText = this.fileNameSpan.dataset.defaultText || this.t.noFile;

      if (file) {
        this.fileNameSpan.textContent = file.name;
        this.selectedFile = file;
      } else {
        this.fileNameSpan.textContent = defaultText;
        this.selectedFile = null;
      }
    }

    async processCSV() {
      this.clearMessages();

      if (!this.selectedFile) {
        this.showError(this.t.chooseFileFirst);

        return;
      }

      if (!this.selectedFile.name.toLowerCase().endsWith('.csv')) {
        this.showError(this.t.invalidCsv);

        return;
      }

      this.setProcessingState(true);

      try {
        const csvText = await this.readFileAsText(this.selectedFile);
        const { isValid, errors, items } = this.validateCSV(csvText);

        if (!isValid) {
          this.showError(errors.join('\n'));

          return;
        }

        await this.addProductsToCart(items);
        this.showSuccess(this.t.success);

        setTimeout(() => {
          this.hideModal();
          this.resetForm();

          window.location.reload();
        }, 2000);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setProcessingState(false);
      }
    }

    setProcessingState(isProcessing) {
      if (this.processButton) {
        this.processButton.disabled = isProcessing;
        this.processButton.textContent = isProcessing ? this.t.processing : this.t.upload;
      }
    }

    resetForm() {
      if (this.fileInput) {
        this.fileInput.value = '';
      }

      if (this.fileNameSpan) {
        const defaultText = this.fileNameSpan.dataset.defaultText || this.t.noFile;

        this.fileNameSpan.textContent = defaultText;
      }

      this.selectedFile = null;
      this.clearMessages();
    }

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error(this.t.readFileFailed));
        reader.readAsText(file);
      });
    }

    validateCSV(csvText) {
      const lines = csvText.trim().split('\n');
      const errors = [];
      const items = [];

      if (lines.length === 0) {
        errors.push(this.t.emptyFile);

        return { isValid: false, errors, items };
      }

      if (lines.length > 100) {
        errors.push(this.t.maxRowsExceeded);

        return { isValid: false, errors, items };
      }

      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i].trim();

        if (line) {
          const columns = line.split(',').map((col) => col.trim());

          if (columns.length !== 2) {
            errors.push(`${this.t.row} ${i + 1}: ${this.t.missingRows}`);
          } else {
            const [sku, quantityStr] = columns;

            if (!sku) {
              // eslint-disable-next-line no-continue
              continue;
            }

            const quantity = parseInt(quantityStr, 10);
            const itemErrors = this.validateItem(quantity, i);

            if (itemErrors.length > 0) {
              errors.push(...itemErrors);
            } else {
              items.push({ sku, quantity });
            }
          }
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        items,
      };
    }

    async fetchProductBySku(sku) {
      try {
        const response = await fetch(`${window.theme.appBridgeUrl}/search/product?sku=${sku}`, {
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`${this.t.fetchFailed}: ${response.status}`);
        }

        const productData = await response.json();

        if (
          !productData ||
          !productData.variants ||
          !productData.variants.nodes ||
          productData.variants.nodes.length === 0
        ) {
          return null;
        }

        const variantId = productData.variants.nodes[0].id;
        const variantIdNumber = variantId.split('/').pop();

        return parseInt(variantIdNumber, 10);
      } catch (error) {
        return null;
      }
    }

    async addProductsToCart(items) {
      const cartItems = [];
      const errors = [];

      const variantPromises = items.map((item) => this.fetchProductBySku(item.sku));
      const variantIds = await Promise.all(variantPromises);

      items.forEach((item, index) => {
        const variantId = variantIds[index];

        if (variantId) {
          cartItems.push({
            id: variantId,
            quantity: item.quantity,
          });
        } else {
          errors.push(`${this.t.invalidProduct}: ${item.sku}`);
        }
      });

      if (errors.length > 0) {
        throw new Error(`${this.t.productsNotFound}:\n${errors.join('\n')}`);
      }

      if (cartItems.length === 0) {
        throw new Error(this.t.invalidProducts);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: cartItems }),
        signal: this.abortController.signal,
      }).catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        throw new Error(`Error adding products to cart: ${error.message || error}`);
      });

      if (!response?.ok) {
        const errorData = await response.json().catch(() => ({}));

        throw new Error(errorData.message || `${this.t.addToCartFailed}: ${response.status}`);
      }

      return response?.json() ?? Promise.resolve({});
    }

    clearMessages() {
      const existingMessages = this.querySelectorAll('.bulk-add-products__message');

      existingMessages.forEach((msg) => msg.remove());
    }

    showError(message) {
      this.showMessage(message, 'error');
    }

    showSuccess(message) {
      this.showMessage(message, 'success');
    }

    showMessage(message, type) {
      this.clearMessages();

      const messageDiv = document.createElement('div');

      messageDiv.className = `bulk-add-products__message bulk-add-products__message--${type}`;
      messageDiv.textContent = message;

      const visibleForm = this.querySelector('.bulk-add-products__form:not(.bulk-add-products__form--hidden)');
      const submitWrapper = visibleForm ? visibleForm.querySelector('.bulk-add-products__form--submit') : null;

      if (submitWrapper) {
        submitWrapper.parentNode.insertBefore(messageDiv, submitWrapper);
      }
    }

    bindManualRowEvents() {
      if (this.manualRowsContainer) {
        this.manualRowsContainer.addEventListener(
          'click',
          (e) => {
            if (e.target.classList.contains('bulk-add-products__add-row')) {
              e.stopPropagation();
              this.boundAddRow(e);
            } else if (e.target.classList.contains('bulk-add-products__remove-row')) {
              e.stopPropagation();
              this.boundRemoveRow(e);
            }
          },
          { signal: this.abortController.signal }
        );
      }
    }

    validateItem(quantity, rowIndex) {
      const errors = [];

      if (Number.isNaN(quantity) || quantity <= 0) {
        errors.push(`${this.t.row} ${rowIndex + 1}: ${this.t.invalidQuantity}`);
      }

      return errors;
    }

    updateRowButtons() {
      const rows = this.manualRowsContainer.querySelectorAll('.bulk-add-products__manual-form--row');

      rows.forEach((row, index) => {
        row.querySelectorAll('button').forEach((btn) => btn.remove());

        const isLastRow = index === rows.length - 1;
        const isOnlyRow = rows.length === 1;

        if (isLastRow) {
          if (isOnlyRow) {
            row.insertAdjacentHTML('beforeend', this.ADD_BUTTON);
          } else {
            row.insertAdjacentHTML('beforeend', this.REMOVE_BUTTON + this.ADD_BUTTON);
          }
        } else {
          row.insertAdjacentHTML('beforeend', this.REMOVE_BUTTON);
        }
      });
    }

    addManualRow() {
      const newRow = document.createElement('div');

      newRow.innerHTML = this.rowTemplate;
      this.manualRowsContainer.appendChild(newRow);
      this.updateRowButtons();
    }

    removeManualRow(e) {
      const row = e.target.closest('.bulk-add-products__manual-form--row');
      const rows = this.manualRowsContainer.querySelectorAll('.bulk-add-products__manual-form--row');

      if (rows.length > 1) {
        row.remove();
        this.updateRowButtons();
      }
    }

    async processManualForm() {
      this.clearMessages();

      const rows = this.manualRowsContainer.querySelectorAll('.bulk-add-products__manual-form--row');
      const items = [];
      const errors = [];

      rows.forEach((row, index) => {
        const skuInput = row.querySelector('input[name="sku"]');
        const quantityInput = row.querySelector('input[name="quantity"]');

        const sku = skuInput.value.trim();

        if (!sku) {
          return;
        }

        const quantity = parseInt(quantityInput.value, 10);
        const itemErrors = this.validateItem(quantity, index);

        if (itemErrors.length > 0) {
          errors.push(...itemErrors);
        } else {
          items.push({ sku, quantity });
        }
      });

      if (errors.length > 0) {
        this.showError(errors.join('\n'));

        return;
      }

      if (items.length === 0) {
        this.showError(this.t.addOneProduct);

        return;
      }

      this.setManualProcessingState(true);

      try {
        await this.addProductsToCart(items);
        this.showSuccess(this.t.success);

        setTimeout(() => {
          this.hideModal();
          this.resetManualForm();
          window.location.reload();
        }, 2000);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setManualProcessingState(false);
      }
    }

    setManualProcessingState(isProcessing) {
      if (this.addManualProductsButton) {
        this.addManualProductsButton.disabled = isProcessing;
        this.addManualProductsButton.textContent = isProcessing ? this.t.processing : this.t.addToCart;
      }
    }

    resetManualForm() {
      if (this.manualRowsContainer) {
        this.manualRowsContainer.innerHTML = this.rowTemplate.replace(
          '</div>\n      </div>',
          `${this.ADD_BUTTON}\n        </div>\n      </div>`
        );
      }

      this.clearMessages();
    }

    switchToCSV() {
      if (this.csvForm && this.manualForm) {
        this.csvForm.classList.remove('bulk-add-products__form--hidden');
        this.manualForm.classList.add('bulk-add-products__form--hidden');
      }
    }

    switchToManual() {
      if (this.csvForm && this.manualForm) {
        this.csvForm.classList.add('bulk-add-products__form--hidden');
        this.manualForm.classList.remove('bulk-add-products__form--hidden');
      }
    }
  }

  customElements.define('bulk-add-products', BulkAddProducts);
})();
